import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { InputField } from './InputField';
import { FormFooter } from './FormFooter';

export interface PassengerData {
  id?: string;
  firstName: string;
  lastName: string;
  type: 'Adulto' | 'Niño';
  age?: string;
}

interface PassengerFormProps {
  onSave: (passenger: PassengerData) => void;
  onCancel: () => void;
  onDelete?: () => void;
  initialData?: PassengerData;
  mode: 'add' | 'edit';
}

export const PassengerForm = ({
  onSave,
  onCancel,
  onDelete,
  initialData,
  mode
}: PassengerFormProps) => {
  const [formData, setFormData] = useState<PassengerData>(
    initialData || {
      firstName: '',
      lastName: '',
      type: 'Adulto',
      age: ''
    }
  );
  const [errors, setErrors] = useState({ firstName: '', lastName: '' });
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = () => {
    const newErrors = { firstName: '', lastName: '' };

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Este campo es obligatorio';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Este campo es obligatorio';
    }

    setErrors(newErrors);

    if (newErrors.firstName || newErrors.lastName) {
      return;
    }

    setIsLoading(true);

    // Simular guardado
    setTimeout(() => {
      onSave(formData);
      setIsLoading(false);
    }, 800);
  };

  const isFormValid = formData.firstName.trim() && formData.lastName.trim();

  return (
    <div className="space-y-6">
      <InputField
        label="Nombre(s)"
        value={formData.firstName}
        onChange={(val) => {
          setFormData({ ...formData, firstName: val });
          setErrors({ ...errors, firstName: '' });
        }}
        placeholder="Nombre(s) completo(s)"
        error={errors.firstName}
        helperText={!errors.firstName ? 'Como aparece en la identificación' : undefined}
        disabled={isLoading}
      />

      <InputField
        label="Apellido paterno"
        value={formData.lastName}
        onChange={(val) => {
          setFormData({ ...formData, lastName: val });
          setErrors({ ...errors, lastName: '' });
        }}
        placeholder="Apellido paterno"
        error={errors.lastName}
        disabled={isLoading}
      />

      {/* Type Selector */}
      <div className="space-y-2">
        <label className="block text-sm font-semibold text-[#111827]">
          Tipo de pasajero
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setFormData({ ...formData, type: 'Adulto' })}
            disabled={isLoading}
            className={`py-3 px-4 rounded-[12px] font-semibold transition-all ${
              formData.type === 'Adulto'
                ? 'bg-[#FF6A2A] text-white border-2 border-[#FF6A2A]'
                : 'bg-white text-[#6B7280] border-2 border-[#E5E7EB] hover:border-[#FF6A2A]'
            } disabled:opacity-50`}
          >
            Adulto
          </button>
          <button
            type="button"
            onClick={() => setFormData({ ...formData, type: 'Niño' })}
            disabled={isLoading}
            className={`py-3 px-4 rounded-[12px] font-semibold transition-all ${
              formData.type === 'Niño'
                ? 'bg-[#FF6A2A] text-white border-2 border-[#FF6A2A]'
                : 'bg-white text-[#6B7280] border-2 border-[#E5E7EB] hover:border-[#FF6A2A]'
            } disabled:opacity-50`}
          >
            Niño
          </button>
        </div>
      </div>

      {/* Optional Age */}
      <InputField
        label="Edad (opcional)"
        value={formData.age || ''}
        onChange={(val) => {
          const cleaned = val.replace(/\D/g, '').slice(0, 3);
          setFormData({ ...formData, age: cleaned });
        }}
        type="number"
        placeholder="Ej: 8"
        helperText="Ayuda a las líneas de autobús con políticas específicas"
        disabled={isLoading}
      />

      {/* Delete Button (only in edit mode) */}
      {mode === 'edit' && onDelete && (
        <div className="pt-4 border-t border-[#E5E7EB]">
          <button
            type="button"
            onClick={onDelete}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 text-[#DC2626] bg-[#FEF2F2] hover:bg-[#FEE2E2] rounded-[12px] font-semibold transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-5 h-5" />
            Eliminar pasajero
          </button>
        </div>
      )}

      <FormFooter
        onCancel={onCancel}
        onSave={handleSave}
        saveLabel={mode === 'add' ? 'Agregar pasajero' : 'Guardar cambios'}
        isLoading={isLoading}
        disabled={!isFormValid}
        variant="stacked"
      />
    </div>
  );
};
